import React, { useEffect, useState } from "react";
import { Megaphone, Plus, Rocket, Percent, MessageSquare, CalendarDays, Radio, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { PageHero, Section, StatCard, Badge, Button, Input, Select, Field, DataTable, EmptyState } from "@/components/premium/UI";
import { Pattern } from "@/components/premium/Patterns";
import { toast } from "sonner";

const TABS = [
  { key: "campaigns",   label: "Campaigns",   icon: Rocket,       endpoint: "campaigns" },
  { key: "promotions",  label: "Promotions",  icon: Percent,      endpoint: "promotions" },
  { key: "engagements", label: "Engagements", icon: MessageSquare, endpoint: "engagements" },
  { key: "events",      label: "Events",      icon: CalendarDays, endpoint: "events" },
  { key: "ads",         label: "Ads",         icon: Radio,        endpoint: "ads" },
  { key: "social",      label: "Social",      icon: Share2,       endpoint: "social" },
];

export default function Marketing() {
  const { has } = usePermissions();
  const canWrite = has("data:marketing:write");
  const [tab, setTab] = useState("campaigns");
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [showForm, setShowForm] = useState(false);
  const t = TABS.find(x => x.key === tab);

  useEffect(() => { api.get("/marketing/dashboard").then(r => setStats(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    api.get(`/marketing/${t.endpoint}`).then(r => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
    setShowForm(false); setForm({});
  }, [tab, t.endpoint]);

  async function submit(e) {
    e.preventDefault();
    try {
      await api.post(`/marketing/${t.endpoint}`, form);
      toast.success("Saved");
      const { data } = await api.get(`/marketing/${t.endpoint}`);
      setRows(data || []);
      setShowForm(false); setForm({});
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  }

  return (
    <div className="space-y-6" data-testid="marketing-page">
      <PageHero
        eyebrow="Marketing360"
        title="Marketing & Growth"
        subtitle="Campaigns, promotions, customer engagement, events, ads and social planning — one workspace for the entire commercial engine."
        moduleKind="marketing"
        actions={canWrite && <Button variant="primary" onClick={() => setShowForm(v => !v)} data-testid="marketing-add-btn"><Plus className="w-4 h-4" /> Add {t.label.replace(/s$/, "").toLowerCase()}</Button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard label="Active campaigns" value={stats?.active_campaigns ?? 0} icon={Rocket} tone="info" testid="marketing-stat-active" />
        <StatCard label="Scheduled campaigns" value={stats?.scheduled_campaigns ?? 0} icon={CalendarDays} tone="accent" testid="marketing-stat-scheduled" />
        <StatCard label="Upcoming events" value={stats?.upcoming_events ?? 0} icon={CalendarDays} tone="primary" testid="marketing-stat-events" />
        <StatCard label="Open engagements" value={stats?.open_engagements ?? 0} icon={MessageSquare} tone={stats?.open_engagements > 0 ? "danger" : "success"} testid="marketing-stat-engagements" />
      </div>

      <Section className="relative overflow-hidden">
        <Pattern kind="marketing" />
        <div className="relative">
          <div className="flex flex-wrap gap-1.5 mb-5 p-1 rounded-full border border-border bg-muted/40 w-fit">
            {TABS.map(x => (
              <button
                key={x.key}
                data-testid={`marketing-tab-${x.key}`}
                onClick={() => setTab(x.key)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${tab === x.key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              >
                <x.icon className="w-3.5 h-3.5" /> {x.label}
              </button>
            ))}
          </div>

          {showForm && <MForm tab={tab} form={form} setForm={setForm} submit={submit} onCancel={() => setShowForm(false)} />}

          {loading ? (
            <div className="animate-pulse space-y-2">{[1,2,3].map(i => <div key={i} className="h-10 bg-muted/40 rounded-lg" />)}</div>
          ) : rows.length === 0 ? (
            <EmptyState icon={Megaphone} title={`No ${tab} yet`} description={canWrite ? "Add the first record using the button above." : "Records will appear here once created."} />
          ) : (
            <DataTable
              testid={`marketing-table-${tab}`}
              columns={cols(tab)}
              rows={rows}
            />
          )}
        </div>
      </Section>
    </div>
  );
}

function cols(tab) {
  switch (tab) {
    case "campaigns": return [
      { header: "Name", cell: r => <span className="font-medium">{r.name}</span> },
      { header: "Channel", cell: r => <Badge variant="primary">{r.channel}</Badge> },
      { header: "Start", key: "start_date" },
      { header: "End", key: "end_date" },
      { header: "Budget", cell: r => r.budget ? `$${Number(r.budget).toLocaleString()}` : "—" },
      { header: "Status", cell: r => <Badge variant={r.status === "active" ? "healthy" : r.status === "completed" ? "primary" : r.status === "paused" ? "pending" : "draft"}>{r.status}</Badge> },
    ];
    case "promotions": return [
      { header: "Title", cell: r => <span className="font-medium">{r.title}</span> },
      { header: "Discount", cell: r => `${r.discount_value}${r.discount_type === "percent" ? "%" : ""}` },
      { header: "From", key: "start_date" },
      { header: "To", key: "end_date" },
      { header: "Applies", key: "applies_to" },
      { header: "Status", cell: r => <Badge variant={r.status === "active" ? "healthy" : "draft"}>{r.status}</Badge> },
    ];
    case "engagements": return [
      { header: "Customer", cell: r => <span className="font-medium">{r.customer_name}</span> },
      { header: "Channel", cell: r => <Badge variant="primary">{r.channel}</Badge> },
      { header: "Type", key: "engagement_type" },
      { header: "Sentiment", cell: r => <Badge variant={r.sentiment === "positive" ? "healthy" : r.sentiment === "negative" ? "critical" : "neutral"}>{r.sentiment}</Badge> },
      { header: "Summary", cell: r => <span className="line-clamp-1">{r.summary}</span> },
      { header: "Resolved", cell: r => <Badge variant={r.resolved ? "healthy" : "pending"}>{r.resolved ? "Yes" : "Open"}</Badge> },
    ];
    case "events": return [
      { header: "Name", cell: r => <span className="font-medium">{r.name}</span> },
      { header: "Date", key: "event_date" },
      { header: "Venue", key: "venue" },
      { header: "Audience", key: "audience" },
      { header: "Budget", cell: r => r.budget ? `$${Number(r.budget).toLocaleString()}` : "—" },
      { header: "Status", cell: r => <Badge variant={r.status === "completed" ? "healthy" : r.status === "planned" ? "primary" : "pending"}>{r.status}</Badge> },
    ];
    case "ads": return [
      { header: "Platform", cell: r => <Badge variant="primary">{r.platform}</Badge> },
      { header: "Creative", key: "creative" },
      { header: "Start", key: "start_date" },
      { header: "Spend", cell: r => `$${Number(r.spend || 0).toLocaleString()}` },
      { header: "Reach", key: "reach" },
      { header: "Status", cell: r => <Badge variant={r.status === "active" ? "healthy" : "draft"}>{r.status}</Badge> },
    ];
    case "social": return [
      { header: "Platform", cell: r => <Badge variant="primary">{r.platform}</Badge> },
      { header: "Scheduled", key: "scheduled_at" },
      { header: "Caption", cell: r => <span className="line-clamp-1">{r.caption}</span> },
      { header: "Status", cell: r => <Badge variant={r.status === "posted" ? "healthy" : "pending"}>{r.status}</Badge> },
    ];
    default: return [];
  }
}

function MForm({ tab, form, setForm, submit, onCancel }) {
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <form onSubmit={submit} className="rounded-2xl border border-border bg-muted/30 p-5 mb-5 grid grid-cols-1 md:grid-cols-3 gap-3">
      {tab === "campaigns" && (<>
        <Field label="Name"><Input value={form.name || ""} onChange={e => upd("name", e.target.value)} required data-testid="marketing-form-name" /></Field>
        <Field label="Objective"><Input value={form.objective || ""} onChange={e => upd("objective", e.target.value)} /></Field>
        <Field label="Channel">
          <Select value={form.channel || "mixed"} onChange={e => upd("channel", e.target.value)}>
            {["mixed", "sms", "whatsapp", "social", "radio", "flyer"].map(v => <option key={v}>{v}</option>)}
          </Select>
        </Field>
        <Field label="Audience"><Input value={form.audience || ""} onChange={e => upd("audience", e.target.value)} /></Field>
        <Field label="Start"><Input type="date" value={form.start_date || ""} onChange={e => upd("start_date", e.target.value)} required /></Field>
        <Field label="End"><Input type="date" value={form.end_date || ""} onChange={e => upd("end_date", e.target.value)} /></Field>
        <Field label="Budget"><Input type="number" value={form.budget || ""} onChange={e => upd("budget", parseFloat(e.target.value))} /></Field>
      </>)}
      {tab === "promotions" && (<>
        <Field label="Title"><Input value={form.title || ""} onChange={e => upd("title", e.target.value)} required /></Field>
        <Field label="Discount type">
          <Select value={form.discount_type || "percent"} onChange={e => upd("discount_type", e.target.value)}>
            {["percent", "flat", "bundle"].map(v => <option key={v}>{v}</option>)}
          </Select>
        </Field>
        <Field label="Discount value"><Input type="number" step="0.01" value={form.discount_value || ""} onChange={e => upd("discount_value", parseFloat(e.target.value))} required /></Field>
        <Field label="Start"><Input type="date" value={form.start_date || ""} onChange={e => upd("start_date", e.target.value)} required /></Field>
        <Field label="End"><Input type="date" value={form.end_date || ""} onChange={e => upd("end_date", e.target.value)} required /></Field>
        <Field label="Applies to"><Input value={form.applies_to || ""} onChange={e => upd("applies_to", e.target.value)} /></Field>
      </>)}
      {tab === "engagements" && (<>
        <Field label="Customer"><Input value={form.customer_name || ""} onChange={e => upd("customer_name", e.target.value)} required /></Field>
        <Field label="Channel">
          <Select value={form.channel || "whatsapp"} onChange={e => upd("channel", e.target.value)}>
            {["whatsapp", "phone", "in_person", "sms", "email"].map(v => <option key={v}>{v}</option>)}
          </Select>
        </Field>
        <Field label="Type">
          <Select value={form.engagement_type || "outreach"} onChange={e => upd("engagement_type", e.target.value)}>
            {["outreach", "complaint", "feedback", "inquiry"].map(v => <option key={v}>{v}</option>)}
          </Select>
        </Field>
        <Field label="Sentiment">
          <Select value={form.sentiment || "neutral"} onChange={e => upd("sentiment", e.target.value)}>
            {["positive", "neutral", "negative"].map(v => <option key={v}>{v}</option>)}
          </Select>
        </Field>
        <Field label="Summary" className="md:col-span-3"><Input value={form.summary || ""} onChange={e => upd("summary", e.target.value)} required /></Field>
      </>)}
      {tab === "events" && (<>
        <Field label="Name"><Input value={form.name || ""} onChange={e => upd("name", e.target.value)} required /></Field>
        <Field label="Date"><Input type="date" value={form.event_date || ""} onChange={e => upd("event_date", e.target.value)} required /></Field>
        <Field label="Venue"><Input value={form.venue || ""} onChange={e => upd("venue", e.target.value)} /></Field>
        <Field label="Audience"><Input value={form.audience || ""} onChange={e => upd("audience", e.target.value)} /></Field>
        <Field label="Budget"><Input type="number" value={form.budget || ""} onChange={e => upd("budget", parseFloat(e.target.value))} /></Field>
      </>)}
      {tab === "ads" && (<>
        <Field label="Platform"><Input value={form.platform || ""} onChange={e => upd("platform", e.target.value)} required /></Field>
        <Field label="Creative"><Input value={form.creative || ""} onChange={e => upd("creative", e.target.value)} /></Field>
        <Field label="Start"><Input type="date" value={form.start_date || ""} onChange={e => upd("start_date", e.target.value)} required /></Field>
        <Field label="Spend"><Input type="number" step="0.01" value={form.spend || 0} onChange={e => upd("spend", parseFloat(e.target.value))} /></Field>
      </>)}
      {tab === "social" && (<>
        <Field label="Platform">
          <Select value={form.platform || "facebook"} onChange={e => upd("platform", e.target.value)}>
            {["facebook", "instagram", "whatsapp_status", "x", "tiktok"].map(v => <option key={v}>{v}</option>)}
          </Select>
        </Field>
        <Field label="Scheduled at"><Input type="datetime-local" value={form.scheduled_at || ""} onChange={e => upd("scheduled_at", e.target.value)} required /></Field>
        <Field label="Caption" className="md:col-span-3"><Input value={form.caption || ""} onChange={e => upd("caption", e.target.value)} /></Field>
      </>)}
      <div className="md:col-span-3 flex justify-end gap-2 mt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="primary" data-testid="marketing-form-submit">Save</Button>
      </div>
    </form>
  );
}
